import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, type Brand, type SocialProvider } from "../../lib/api";
import { SocialIcon } from "../../components/SocialIcon";

const ALL_PROVIDERS: SocialProvider[] = [
  "meta_instagram",
  "meta_facebook",
  "linkedin",
  "x",
];

const PROVIDER_LABEL: Record<SocialProvider, string> = {
  meta_instagram: "Instagram",
  meta_facebook: "Facebook",
  linkedin: "LinkedIn",
  x: "X",
  bluesky: "Bluesky",
};

export default function BrandList() {
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Map of brandId → set of connected provider ids
  const [brandConnections, setBrandConnections] = useState<
    Record<string, Set<SocialProvider>>
  >({});

  useEffect(() => {
    api.brands
      .list()
      .then((r) => {
        setBrands(r.brands);
        // Fetch connections for all brands in parallel (fire-and-forget; UI
        // shows greyed icons immediately and fills in as each call resolves)
        r.brands.forEach((b) => {
          api.social
            .list(b.brandId)
            .then(({ connections }) => {
              setBrandConnections((prev) => ({
                ...prev,
                [b.brandId]: new Set(connections.map((c) => c.provider)),
              }));
            })
            .catch(() => {
              // Non-fatal — icon row just stays greyed out
            });
        });
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const handleDelete = async (brandId: string, name: string) => {
    if (
      !confirm(`Delete "${name}" and all its campaigns? This cannot be undone.`)
    )
      return;
    await api.brands.delete(brandId);
    setBrands((prev) => prev.filter((b) => b.brandId !== brandId));
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-4 mb-8">
        <h1 className="page-title">Brands</h1>
        <Link to="/brands/new" className="btn-primary whitespace-nowrap">
          + New brand
        </Link>
      </div>

      <div className="bg-brand-50 border border-brand-100 rounded-xl px-5 py-4 mb-8">
        <p className="text-sm text-brand-800 font-medium mb-1">
          What is a brand?
        </p>
        <p className="text-sm text-brand-700 leading-relaxed">
          A brand is the identity your AI knows before writing a single post.
          Its voice, industry, audience, and colors are injected directly into
          every content generation prompt — so the more detail you add, the more
          on-brand your output will be. Each brand can hold multiple campaigns
          across different platforms.
        </p>
        <p className="text-xs text-brand-500 mt-2">
          Managing multiple clients or product lines? Create a separate brand
          for each.{" "}
          {brands.length > 0 && (
            <span>
              Your current plan supports up to{" "}
              {brands.length === 1
                ? "1 brand — upgrade"
                : `${brands.length} brands`}
              .
            </span>
          )}
        </p>
      </div>

      {loading && <p className="text-faint text-sm">Loading…</p>}
      {error && <p className="text-red-600 text-sm">{error}</p>}

      {!loading && brands.length === 0 && (
        <div className="card p-10 text-center">
          <p className="text-muted font-medium mb-1">No brands yet</p>
          <p className="text-faint text-sm mb-5">
            Paste in a website or product URL and the AI will fill in the
            details for you.
          </p>
          <Link to="/brands/new" className="btn-primary">
            Create your first brand
          </Link>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {brands.map((brand) => {
          const connected = brandConnections[brand.brandId];
          const connectedCount = connected ? connected.size : 0;

          return (
            <div
              key={brand.brandId}
              className="card p-5 hover:border-gray-300 transition-colors"
            >
              {/* Top row: brand identity + action buttons */}
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div
                    className="w-10 h-10 rounded-lg flex-shrink-0 brand-swatch"
                    style={
                      {
                        "--brand-primary": brand.colors.primary,
                      } as React.CSSProperties
                    }
                  />
                  <div className="min-w-0">
                    <p className="font-semibold text-ink truncate">
                      {brand.name}
                    </p>
                    <p className="text-xs text-faint capitalize">
                      {brand.voice} · {brand.industry || "No industry"}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <Link
                    to={`/brands/${brand.brandId}/campaigns`}
                    className="btn-primary text-xs"
                    title="View campaigns and generate content for this brand"
                    aria-label={`View campaigns for ${brand.name}`}
                  >
                    Campaigns →
                  </Link>
                  <Link
                    to={`/brands/${brand.brandId}/edit`}
                    className="btn-secondary text-xs"
                  >
                    Edit
                  </Link>
                  <button
                    onClick={() => handleDelete(brand.brandId, brand.name)}
                    className="text-xs text-red-500 hover:text-red-700 px-2"
                    aria-label={`Delete ${brand.name}`}
                  >
                    ×
                  </button>
                </div>
              </div>

              {/* Social connections row */}
              <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  {ALL_PROVIDERS.map((provider) => {
                    const isConnected = connected?.has(provider) ?? false;
                    return (
                      <div
                        key={provider}
                        title={`${PROVIDER_LABEL[provider]}${isConnected ? " — connected" : " — not connected"}`}
                        style={{ opacity: isConnected ? 1 : 0.2 }}
                        className="transition-opacity"
                      >
                        <SocialIcon provider={provider} size={24} />
                      </div>
                    );
                  })}
                  <span className="text-xs text-faint ml-1">
                    {connected === undefined
                      ? "Loading…"
                      : connectedCount === 0
                        ? "No channels connected"
                        : `${connectedCount} of ${ALL_PROVIDERS.length} connected`}
                  </span>
                </div>
                <Link
                  to={`/settings/platforms?brandId=${brand.brandId}`}
                  className="text-xs font-medium text-brand-600 hover:text-brand-700 whitespace-nowrap"
                >
                  {connectedCount === 0 ? "Connect channels →" : "Manage →"}
                </Link>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
